import {
	autoUpdate,
	flip,
	offset as offsetMiddleware,
	shift,
	useFloating,
	useFocus,
	useHover,
	useDismiss,
	useInteractions,
	useMergeRefs,
	useRole,
	useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';
import { cloneElement, isValidElement, useState } from 'react';
import { createPortal } from 'react-dom';

import s from './Tooltip.module.css';

export const Tooltip = ({ children, title, ...props }) => {
	const { position, align, offset, crossAxisOffset, delayShow, delayHide, radius, arrow } = props;

	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: position,
		strategy: 'fixed',

		middleware: [
			offsetMiddleware({ mainAxis: offset, crossAxis: crossAxisOffset ?? 0 }),
			flip(),
			shift(),
		],
		whileElementsMounted: autoUpdate,
	});

	const hover = useHover(context, {
		move: false,
		delay: { open: delayShow, close: delayHide },
	});
	const focus = useFocus(context);
	const dismiss = useDismiss(context);

	const role = useRole(context, { role: 'tooltip' });

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

	const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
		duration: 200,
		initial: { opacity: 0 },
		open: { opacity: 1 },
		close: { opacity: 0 },
	});

	const arrowEl = (
		<div className={s.tooltip_arrowWrap} data-position={position} data-align={align}>
			<div className={s.tooltip_arrow} />
		</div>
	);

	const isArrowFirst = position === 'bottom' || position === 'right';

	const childRef = isValidElement(children) ? children.props.ref : null;
	const mergedRef = useMergeRefs([refs.setReference, childRef]);

	const trigger = isValidElement(children) ? (
		cloneElement(children, getReferenceProps({ ...children.props, ref: mergedRef }))
	) : (
		<span ref={mergedRef} {...getReferenceProps()}>
			{children}
		</span>
	);

	return (
		<>
			{trigger}
			{isMounted &&
				createPortal(
					<div
						ref={refs.setFloating}
						className={s.tooltip}
						style={{ ...floatingStyles, ...transitionStyles }}
						data-position={position}
						data-radius={radius}
						{...getFloatingProps()}
					>
						{isArrowFirst && arrow && arrowEl}
						<div className={clsx(s.tooltip_body, 'body-regular-sm')}>{title}</div>
						{!isArrowFirst && arrow && arrowEl}
					</div>,
					document.body,
				)}
		</>
	);
};
